import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { OrganizationConfig } from '@/api';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, InputTextCell, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { Entity } from '@/models/entity';
import { useUpdateEntityOrganization } from '@/models/entityOrganization';
import { useCurrentOrganization } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';

type CustomFieldSetting = NonNullable<
  NonNullable<OrganizationConfig['customFields']>[keyof NonNullable<
    OrganizationConfig['customFields']
  >]
>[number];

type EntityOrganization = Entity['entityOrganizations'][number];

export const CustomFields = ({
  entity,
  trait,
}: {
  entity: Entity;
  trait: 'store' | 'workshop';
}) => {
  const [organization] = useCurrentOrganization();
  const { t } = useLingui();

  const customFieldsSettings = organization?.config.customFields?.[trait];
  const entityOrganization = entity.entityOrganizations.find(
    (entityOrganization) => entityOrganization.trait === trait
  )!;

  if (!customFieldsSettings || customFieldsSettings.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Title as="h3" size="300" style={{ paddingBottom: '16px' }}>
        <Trans id="settings.entities.custom-fields.label">Custom Fields</Trans>
      </Title>
      <Table
        ariaLabel={t({
          id: 'settings.entities.custom-fields.table.aria-label',
          message: 'Custom Fields',
        })}
        columnWidths={['1fr', '1fr']}
        bordered
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.entities.custom-fields.table.column.name">Name</Trans>
            </Column>
            <Column>
              <Trans id="settings.entities.custom-fields.table.column.value">Value</Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {customFieldsSettings?.map((field) => (
            <CustomField
              key={field.id}
              fieldSetting={field}
              entityOrganization={entityOrganization}
            />
          ))}
        </Body>
      </Table>
    </Stack>
  );
};

const CustomField = ({
  fieldSetting,
  entityOrganization,
}: {
  fieldSetting: CustomFieldSetting;
  entityOrganization: EntityOrganization;
}) => {
  const { _db } = useDatabaseTranslation();
  const [value, setValue] = useState(entityOrganization.customFields?.[fieldSetting.id]);
  const {
    mutateAsync: updateEntityOrganization,
    isPending,
    variables,
  } = useUpdateEntityOrganization();

  const handleBlur = () => {
    updateEntityOrganization({
      id: entityOrganization.id,
      body: { customFields: { [fieldSetting.id]: value } },
    });
  };

  return (
    <Row>
      <Cell>
        <Paragraph weight="medium">{_db(fieldSetting.label)}</Paragraph>
      </Cell>
      <InputTextCell
        aria-label={_db(fieldSetting.label)}
        value={value}
        onChange={setValue}
        onBlur={handleBlur}
      >
        {isPending
          ? variables?.body.customFields?.[fieldSetting.id]
          : entityOrganization.customFields?.[fieldSetting.id] || '-'}
      </InputTextCell>
    </Row>
  );
};
